import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Heart, MailCheck, Gift, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";

const MSG = {
  expired: ["That confirmation link has expired", "Links last 48 hours. Head back to the pup's page and tap the heart again for a fresh one."],
  closed: ["Voting has closed for that month", "Only hearts cast before 11:59pm Brisbane time on the last day (and confirmed within 24 hours) can count."],
  invalid: ["We couldn't find that confirmation", "Tap the heart on the pup's page again and we'll send a new link."],
};
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" }); } catch { return ""; } };

function VoterCode({ code, expires, discount }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(code); } catch { /* clipboard blocked */ }
    setCopied(true); toast.success("Code copied — see you at checkout! 🐾"); setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="mt-5 rounded-2xl border-2 border-dashed border-[#19324A] bg-[#FFF9EE] p-4 text-left" data-testid="pup-voter-code">
      <p className="text-xs font-bold text-[#19324A]/70 inline-flex items-center gap-1.5"><Gift className="w-4 h-4 text-[#FF6B9D]" /> A little thank-you from Chewie &amp; Solo — {discount}% off your next order</p>
      <div className="mt-2 flex items-center gap-2">
        <span className="font-display font-extrabold text-xl sm:text-2xl tracking-widest text-[#19324A] flex-1 whitespace-nowrap" data-testid="pup-voter-code-value">{code}</span>
        <button type="button" onClick={copy} className="inline-flex items-center gap-1.5 rounded-full bg-[#19324A] text-white text-xs font-bold px-3 py-2" data-testid="pup-voter-code-copy">{copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy"}</button>
      </div>
      <p className="text-[11px] text-[#19324A]/60 mt-2">Single use · valid until {fmtDate(expires)} · we've emailed it to you too.</p>
    </div>
  );
}

// Email links land here; the heart only counts when the person presses the button (defeats link pre-scanners).
export default function PupConfirmPage() {
  useSeo("Confirm your heart — Pup of the Month", "One tap to make your Pup of the Month heart count.");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { if (!token) setResult({ status: "invalid", url: "/pup-of-the-month" }); }, [token]);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pup/vote/confirm", { token });
      setResult(data);
      if (data.status === "ok" && !data.code) setTimeout(() => navigate(`${data.url.replace(/^https?:\/\/[^/]+/, "")}?voted=1`), 1400);
    } catch { setResult({ status: "invalid", url: "/pup-of-the-month" }); }
    finally { setBusy(false); }
  };
  const back = result?.url ? result.url.replace(/^https?:\/\/[^/]+/, "") : "/pup-of-the-month";

  return (
    <div className="min-h-[70vh] bg-[#FFF9EE] grid place-items-center p-6" data-testid="pup-confirm-page">
      <div className="bg-white rounded-3xl soft-shadow p-8 max-w-md w-full text-center">
        {!result && (
          <>
            <span className="w-16 h-16 rounded-full bg-[#FF6B9D]/15 inline-flex items-center justify-center"><Heart className="w-8 h-8 text-[#FF6B9D] fill-current" /></span>
            <h1 className="font-display font-extrabold text-2xl text-[#19324A] mt-4">One tap to count your heart 💛</h1>
            <p className="text-sm text-[#19324A]/70 mt-2">Press the button so we know it's really you — then your verified heart goes on the board.</p>
            <button type="button" onClick={confirm} disabled={busy} className="mt-6 min-h-[56px] w-full rounded-full bg-[#FF6B9D] text-white font-display font-extrabold text-lg inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="pup-confirm-button"><Heart className="w-5 h-5 fill-current" /> {busy ? "Counting…" : "Yes, count my heart"}</button>
          </>
        )}
        {result?.status === "ok" && (
          <div data-testid="pup-confirm-ok">
            <span className="w-16 h-16 rounded-full bg-[#7ED957] inline-flex items-center justify-center"><MailCheck className="w-8 h-8 text-[#19324A]" /></span>
            <h1 className="font-display font-extrabold text-2xl text-[#19324A] mt-4">{result.already ? "Already counted!" : "Your heart is confirmed! ❤️"}</h1>
            <p className="text-sm text-[#19324A]/70 mt-2">{result.pup_name ? `${result.pup_name} is one paw closer to the crown.` : "Thanks for voting."}{result.code ? "" : " Taking you to the pup's page…"}</p>
            {result.code && <VoterCode code={result.code} expires={result.code_expires} discount={result.discount} />}
            {result.code && <Link to={`${back}?voted=1`} className="btn-primary mt-5 inline-flex" data-testid="pup-confirm-continue">Back to {result.pup_name || "the pup"}'s page</Link>}
          </div>
        )}
        {result && result.status !== "ok" && (
          <div data-testid={`pup-confirm-${result.status}`}>
            <h1 className="font-display font-extrabold text-2xl text-[#19324A]">{MSG[result.status][0]}</h1>
            <p className="text-sm text-[#19324A]/70 mt-2">{MSG[result.status][1]}</p>
            <Link to={back} className="btn-primary mt-6 inline-flex" data-testid="pup-confirm-back">Back to the pup's page</Link>
          </div>
        )}
      </div>
    </div>
  );
}
